import type {MouseEvent} from 'react';
import type {StateCreator} from 'zustand';
import {List} from 'immutable';
import type {QuestionSetModelV1} from '@albert-io/models';

import type {ContentDiscoveryStoreState} from 'client/components/ContentDiscovery/store/ContentDiscoveryStore.types';

type DraftState = {
  draftName: string;
  draftNameError: boolean;
  draftSort: string | null;
  isDraft: boolean;
  draftQuestionsQuery: string;
  isQueryLoading: boolean;
  selectedQuestionSets: List<QuestionSetModelV1>;
  totalQuestionCount: number;
  isQuestionsQueryLoading: boolean;
  shouldRandomizeQuestions: boolean;
  numSmartAssignQuestions: number;
  legacyItemId: string | null;
  legacyQuestionsIds?: Array<string>;
  legacyQuestionsIdsExcluded?: Array<string>;
  legacyItemType: 'folder' | 'guide' | null;
  // this set is a copy of the original question sets fetched when pre-filling from a legacy item; used when navigating back to legacy flow
  legacyQuestionSets: List<QuestionSetModelV1>;
};

type DraftActions = {
  setDraftName: (name: string) => void;
  setDraftNameError: (error: boolean) => void;
  setDraftSort: (sort: string) => void;
  setIsDraft: (isDraft: boolean) => void;
  setDraftQuestionsQuery: (query: string) => void;
  setIsQueryLoading: (queryLoading: boolean) => void;
  removeQuestionSet: (
    e: MouseEvent<HTMLElement>,
    questionSet: QuestionSetModelV1 | List<QuestionSetModelV1>,
    onSuccess?: () => void
  ) => Promise<void>;
  isQuestionSetSelected: (questionSet: QuestionSetModelV1) => boolean;
  setSelectedQuestionSets: (questionSets: List<QuestionSetModelV1>) => void;
  addSelectedQuestionSets: (questionSets: List<QuestionSetModelV1>) => void;
  getTotalQuestionCount: () => number;
  setTotalQuestionCount: (questionCount: number) => void;
  setIsQuestionsQueryLoading: (isLoading: boolean) => void;
  setShouldRandomizeQuestions: (shouldRandomize: boolean) => void;
  setNumSmartAssignQuestions: (numQuestions: number) => void;
  setLegacyItemId: (itemId: string | null) => void;
  setLegacyQuestionsIds: (questionIds: Array<string>) => void;
  setLegacyQuestionsIdsExcluded: (questionIds: Array<string>) => void;
  setLegacyItemType: (itemType: 'folder' | 'guide' | null) => void;
  setLegacyQuestionSets: (questionSets: List<QuestionSetModelV1>) => void;
  reset: () => void;
};

const initialState: DraftState = {
  draftName: '',
  draftNameError: false,
  draftSort: null,
  isDraft: false,
  draftQuestionsQuery: '',
  isQueryLoading: false,
  selectedQuestionSets: List(),
  totalQuestionCount: 0,
  isQuestionsQueryLoading: false,
  shouldRandomizeQuestions: false,
  numSmartAssignQuestions: 0,
  legacyItemId: null,
  legacyQuestionsIds: [],
  legacyQuestionsIdsExcluded: [],
  legacyItemType: null,
  legacyQuestionSets: List()
};

export type DraftSlice = DraftState & DraftActions;

// add type guard to isList function
const isList = <T>(item: T | List<T>): item is List<T> => List.isList(item as List<T>);

export const createDraftSlice: StateCreator<
  ContentDiscoveryStoreState,
  [['zustand/immer', never]],
  [],
  DraftSlice
> = (set, get): DraftSlice => ({
  ...initialState,

  setDraftName: (name) =>
    set((state) => {
      state.draft.draftName = name;
    }),

  setDraftNameError: (error) =>
    set((state) => {
      state.draft.draftNameError = error;
    }),

  setDraftSort: (sort) =>
    set((state) => {
      state.draft.draftSort = sort;
    }),

  setDraftQuestionsQuery: (query) =>
    set((state) => {
      state.draft.draftQuestionsQuery = query;
    }),

  setIsQueryLoading: (queryLoading) =>
    set((state) => {
      state.draft.isQueryLoading = queryLoading;
    }),

  setIsDraft: (isDraft) =>
    set((state) => {
      state.draft.isDraft = isDraft;
    }),

  setSelectedQuestionSets: (questionSets) =>
    set((state) => {
      state.draft.selectedQuestionSets = questionSets;
    }),

  addSelectedQuestionSets: (questionSets) =>
    set((state) => {
      state.draft.selectedQuestionSets = List(
        state.draft.selectedQuestionSets.concat(questionSets)
      );
    }),

  removeQuestionSet: async (e, questionSet) => {
    e.stopPropagation();

    const {draft} = get();
    const {selectedQuestionSets} = draft;

    let filteredQuestionSets;
    if (isList<QuestionSetModelV1>(questionSet)) {
      const ids = questionSet.toArray().map((qSet) => qSet!.getId());
      // for each question set remove
      filteredQuestionSets = selectedQuestionSets.filter(
        (selectedQSet) => !ids.some((id) => id === (selectedQSet!.getId() as string))
      );
    } else {
      filteredQuestionSets = selectedQuestionSets.filter(
        (item) => item!.getId() !== questionSet.getId()
      );
    }
    set((state) => {
      state.draft.selectedQuestionSets = List(filteredQuestionSets);
    });
  },

  isQuestionSetSelected: (questionSet) => {
    const {draft} = get();
    const {selectedQuestionSets} = draft;

    return selectedQuestionSets.some(
      (selectedQSet) => selectedQSet!.getId() === questionSet.getId()
    );
  },

  getTotalQuestionCount: () => {
    const {draft} = get();
    const {selectedQuestionSets, totalQuestionCount, isQueryLoading} = draft;

    return isQueryLoading ? totalQuestionCount : selectedQuestionSets.size;
  },

  setTotalQuestionCount: (questionCount) => {
    set((state) => {
      state.draft.totalQuestionCount = questionCount;
    });
  },

  setIsQuestionsQueryLoading: (isLoading) =>
    set((state) => {
      state.draft.isQuestionsQueryLoading = isLoading;
    }),

  setShouldRandomizeQuestions: (shouldRandomize) =>
    set((state) => {
      state.draft.shouldRandomizeQuestions = shouldRandomize;
    }),

  setNumSmartAssignQuestions: (numQuestions) =>
    set((state) => {
      state.draft.numSmartAssignQuestions = numQuestions;
    }),

  setLegacyItemId: (itemId) =>
    set((state) => {
      state.draft.legacyItemId = itemId;
    }),

  setLegacyQuestionsIds: (questionIds) =>
    set((state) => {
      state.draft.legacyQuestionsIds = questionIds;
    }),

  setLegacyQuestionsIdsExcluded: (questionIds) =>
    set((state) => {
      state.draft.legacyQuestionsIdsExcluded = questionIds;
    }),

  setLegacyItemType: (itemType) =>
    set((state) => {
      state.draft.legacyItemType = itemType;
    }),

  setLegacyQuestionSets: (questionSets) =>
    set((state) => {
      state.draft.legacyQuestionSets = questionSets;
    }),

  reset: () =>
    set((state) => {
      state.draft = {...state.draft, ...initialState};
    })
});
